const CASHFLOW_REPORT = "/reporting/cashflow";
const CASHFLOW_REPORT_DOWNLOADS = "/cashflow-report-downloads";
const FUNDING = "/funding";
const FUNDING_RECEIVABLES = "/funding/receivables";
const FUNDING_TRANSACTIONS = "/funding/transactions";
const INTEGRATIONS = "/integrations";
const INVITATIONS = "/invitations";
const INVOICE_DOWNLOADS = "/invoice-downloads";
const JOIN_REQUESTS = "/join-requests";
const PAYOUT_REQUESTS = "/payout-requests";
const PAYOUT_REQUESTS_FOR_REVIEW = "/payout-requests/categories/review";
const PAYOUTS = "/payouts";
const PAYOUTS_ALL = "/payouts/categories/all";
const PAYOUTS_OVERDUE = "/payouts/categories/overdue";
const PAYOUT_REPORT = "/reporting/payouts";
const PAYOUT_REPORT_DOWNLOADS = "/payout-report-downloads";
const ROSTER = "/roster";
const UNPAID_COMPLETED_WORK_REPORT = "/reporting/unpaid-completed-work";
const UNPAID_COMPLETED_WORK_REPORT_DOWNLOADS = "/unpaid-completed-work-report-downloads";

/**
 * Where a type lands when it carries no subject — either because the notification
 * summarises many records or because the row predates the subject id.
 */
const LIST_DESTINATIONS: Record<string, string> = {
  "bank-account-relink-required-admin": FUNDING,
  "cashflow-report-export-failed-admin": CASHFLOW_REPORT,
  "cashflow-report-export-ready-admin": CASHFLOW_REPORT,
  "deposit-aborted-admin": FUNDING_TRANSACTIONS,
  "fsm-completed-work-abandoned-admin": INTEGRATIONS,
  "fsm-connection-settings-changed-admin": INTEGRATIONS,
  "fsm-tech-settings-changed-admin": INTEGRATIONS,
  "funding-completed-admin": FUNDING_TRANSACTIONS,
  "invitation-undeliverable-admin": INVITATIONS,
  "invoice-failed-admin": PAYOUT_REQUESTS,
  "invoice-ready-admin": PAYOUT_REQUESTS,
  "join-request-received": JOIN_REQUESTS,
  "just-in-time-deposit-initiated-admin": FUNDING,
  "just-in-time-shortfall-admin": FUNDING,
  "low-balance": FUNDING,
  "organization-bank-account-rejected": FUNDING,
  "payee-bank-account-rejected": ROSTER,
  "payout-blocked-admin": PAYOUTS_ALL,
  "payout-overdue-admin": PAYOUTS_OVERDUE,
  "payout-request-submitted-admin": PAYOUT_REQUESTS_FOR_REVIEW,
  "payout-retry-exhausted-admin": PAYOUTS_ALL,
  "payout-terminally-failed-admin": PAYOUTS_ALL,
  "payouts-scheduled": FUNDING_RECEIVABLES,
  "payout-report-failed-admin": PAYOUT_REPORT,
  "payout-report-ready-admin": PAYOUT_REPORT,
  "pending-payout-digest": PAYOUT_REQUESTS_FOR_REVIEW,
  "receivable-overdue-admin": FUNDING_RECEIVABLES,
  "receivable-paid-admin": FUNDING_RECEIVABLES,
  "recurring-deposit-initiated-admin": FUNDING,
  "staff-invitation-received-admin": INVITATIONS,
  "unpaid-completed-work-report-export-failed-admin": UNPAID_COMPLETED_WORK_REPORT,
  "unpaid-completed-work-report-export-ready-admin": UNPAID_COMPLETED_WORK_REPORT,
};

const PAYOUT_SUBJECT_TYPES: readonly string[] = [
  "payout-blocked-admin",
  "payout-overdue-admin",
  "payout-retry-exhausted-admin",
  "payout-terminally-failed-admin",
];

/**
 * A notification identifies its subject with one id whose meaning the type key fixes,
 * so every surface that shows a notification routes from the same pair.
 *
 * `/payout-requests/{id}` resolves the workflow before forwarding, which is why a
 * payout request needs no more than its id here.
 */
export function getNotificationRoute(typeKey: string, subjectId?: string | null): string | null {
  if (subjectId) {
    if (PAYOUT_SUBJECT_TYPES.includes(typeKey)) {
      return `${PAYOUTS}/${subjectId}`;
    }

    switch (typeKey) {
      case "cashflow-report-export-failed-admin":
      case "cashflow-report-export-ready-admin":
        return `${CASHFLOW_REPORT_DOWNLOADS}/${subjectId}`;
      case "invoice-failed-admin":
      case "invoice-ready-admin":
        return `${INVOICE_DOWNLOADS}/${subjectId}`;
      case "payee-bank-account-rejected":
        return `${ROSTER}/${subjectId}/edit`;
      case "payout-request-submitted-admin":
        return `${PAYOUT_REQUESTS}/${subjectId}`;
      case "payout-report-failed-admin":
      case "payout-report-ready-admin":
        return `${PAYOUT_REPORT_DOWNLOADS}/${subjectId}`;
      case "receivable-overdue-admin":
      case "receivable-paid-admin":
        return `${FUNDING_RECEIVABLES}/${subjectId}`;
      case "unpaid-completed-work-report-export-failed-admin":
      case "unpaid-completed-work-report-export-ready-admin":
        return `${UNPAID_COMPLETED_WORK_REPORT_DOWNLOADS}/${subjectId}`;
      default:
        break;
    }
  }

  return LIST_DESTINATIONS[typeKey] ?? null;
}